import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTrash, restoreItem, type TrashModel, type TrashItem } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Undo2, ChevronLeft, ChevronRight } from "lucide-react";

const MODELS: { value: TrashModel; label: string }[] = [
  { value: "Venue", label: "Isletmeler" },
  { value: "LoyaltyRule", label: "Sadakat Kurallari" },
  { value: "Review", label: "Yorumlar" },
];

/**
 * Pull a human-readable label out of a trashed document. Each model exposes
 * different fields, so this is a thin per-model adapter rather than a
 * one-size-fits-all renderer.
 */
function describeItem(model: TrashModel, item: TrashItem): { primary: string; secondary?: string } {
  if (model === "Venue") {
    return {
      primary: String(item.name ?? item._id),
      secondary: typeof item.slug === "string" ? `/${item.slug}` : undefined,
    };
  }
  if (model === "LoyaltyRule") {
    return {
      primary: `${String(item.type)} · ${String(item.trigger)} · ${String(item.points)} puan`,
      secondary: typeof item.rewardDescription === "string" ? item.rewardDescription : undefined,
    };
  }
  // Review (default — TrashModel is closed but TS isn't sure inside a switch).
  const rating = item.rating ? `${item.rating}/5★` : "";
  const comment = typeof item.comment === "string" ? item.comment : "";
  return { primary: `${rating} ${comment}`.trim() || String(item._id) };
}

const LIMIT = 50;

export function TrashPage() {
  const [model, setModel] = useState<TrashModel>("Venue");
  const [page, setPage] = useState(0);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["trash", model, page],
    queryFn: () => getTrash(model, { limit: LIMIT, offset: page * LIMIT }),
  });

  const restore = useMutation({
    mutationFn: ({ id }: { id: string }) => restoreItem(model, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trash", model] }),
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const items = data?.items ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Trash2 className="size-6" /> Geri Donusum
        </h1>
        <span className="text-sm text-muted-foreground">{total} silinmis</span>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Yumusak silinmis kayitlar — 30 gun sonra retention cron tarafindan kalici olarak silinir.
      </p>

      <div className="mb-4 flex gap-2">
        {MODELS.map((m) => (
          <Button
            key={m.value}
            variant={model === m.value ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setModel(m.value);
              setPage(0);
            }}
          >
            {m.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Trash2 className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">Geri donusumde {MODELS.find((m) => m.value === model)?.label.toLowerCase()} yok</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item: TrashItem) => {
            const desc = describeItem(model, item);
            const deletedAt = new Date(item.deletedAt);
            return (
              <Card key={item._id}>
                <CardContent className="flex items-center justify-between gap-4 pt-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{desc.primary}</p>
                    {desc.secondary && <p className="truncate text-xs text-muted-foreground">{desc.secondary}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">
                      Silindi: {deletedAt.toLocaleString("tr-TR")}
                      <Badge variant="outline" className="ml-2">
                        {Math.max(0, 30 - Math.floor((Date.now() - deletedAt.getTime()) / 86400000))} gun kaldi
                      </Badge>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate({ id: item._id })}
                  >
                    <Undo2 className="mr-1 size-4" />
                    Geri Al
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
